import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, tap } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Logger, ConfirmDialogComponent } from 'src/app/@shared';
import { Shopper, ShopperService } from '../..';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';

const log = new Logger('ShopperDetailsComponent');

@Component({
  selector: 'app-shopper-details',
  templateUrl: './shopper-details.component.html',
  styleUrls: ['./shopper-details.component.scss']
})

export class ShopperDetailsComponent<T extends Shopper> implements OnInit {
  shopper$: Observable<T> = of({} as T);
  formGroup = new FormGroup({
    ClientKey: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100),]),
    Id: new FormControl({ value: '0', disabled: true }, { nonNullable: true }),
    FirstName: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100),]),
    LastName: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100),]),
    Email: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100),]),
    Phone: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(255),]),
    Address1: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(255),]),
    Address2: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(255),]),
    City: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(100), Validators.pattern("[a-zA-Z0-9 ]*")]),
    State: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(25), Validators.pattern("[a-zA-Z0-9 ]*")]),
    //Country: new FormControl<string>(''),
    ZipCode: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(100), Validators.pattern("[a-zA-Z0-9]*")]),
    // Latitude: new FormControl<string>(''),
    // Longitude: new FormControl<string>(''),
  });
  shopperId: string = '0';
  name: string = '';

  constructor(private router: Router,
    private route: ActivatedRoute,
    private shopperService: ShopperService<T>,
    private matSnackBar: MatSnackBar,
    private dialog: MatDialog) { }

  ngOnInit(): void {
    log.debug('init');
    // get the store id from the parent route
    this.shopperId = this.route.snapshot.params['shopperId'];

    // if we are editing a record, get the record data
   this.getDataById();
  }

  getDataById(){
    if (this.shopperId && this.shopperId !== '0') {
      this.shopper$ = this.shopperService.getShopper(this.shopperId).pipe(
        tap((shopper) => {
          this.formGroup.patchValue(shopper);
          this.formGroup.markAllAsTouched();
          this.name = shopper.FirstName + ' ' + shopper.LastName

        })
      );
    }
  }

  trimControlValues(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key: string) => {
      const abstractControl = formGroup.get(key);
      if (abstractControl instanceof FormGroup) {
        this.trimControlValues(abstractControl);
      } else {
        if (typeof abstractControl?.value == 'string') {
          abstractControl.setValue(abstractControl?.value.trim());
        }
      }
    })
  }
  
  save() {
    this.trimControlValues(this.formGroup);
    if (this.formGroup.valid) {
      this.shopperService.saveShopper(this.formGroup.getRawValue() as T).subscribe({
        next: (response:any) => {
          this.matSnackBar.open(
            `${this.formGroup.controls.FirstName.value} ${this.formGroup.controls.LastName.value} saved`, 'OK', DEFAULT_SNACKBAR_CONFIG
          );
          this.shopperId = response.Id;
          this.shopperService.reload(); 
          this.getDataById();         
          this.router.navigate([`../../${response.Id}/details`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
        },
        error: (error: any) => {
          if (error.status === 500) {
            log.error('500 Error saving event', error);
          }
          if (error.status === 400) {
            const apiValidations: any = error.error;
            if (Array.isArray(apiValidations)) {
              apiValidations.forEach((validation: any) => {
                if (this.formGroup?.get(validation.PropertyName)) {
                  const control = this.formGroup?.get(validation.PropertyName);
                  if (control) {
                    control.markAsTouched();
                    control.setErrors({ invalid: validation.ErrorMessage });
                    this.matSnackBar.open(validation.ErrorMessage, 'OK', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
                  }
                } else {
                  ///TODO: if we have cross field validation then show the validation error at the top of the screen
                  // if we have cross field validation then show the validation error at the top of the screen
                  // push general error messages to array this is displayed in a toast or dialog
                }
              });
            } else {
              this.matSnackBar.open(apiValidations, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'] });
            }


          }
        }
      });
    }
  }

  cancel(): void {
    this.formGroup.reset();
    this.router.navigate([`../../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
  }

  deleteRecord() {
    const record = this.formGroup.getRawValue();
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Are you sure you wish to delete ${record.LastName}?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe(
      confirmResult => {
        if (confirmResult) {
          this.shopperService.deleteShopper(record.Id).subscribe({
            next: () => {
              this.matSnackBar.open(`${record.FirstName} ${record.LastName} deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
              this.shopperService.reload();
              this.router.navigate([`../../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
            },
            error: (error) => {
              log.error('Error deleting shopper', error);

              if (error.error.value) {
                throw new Error(error.error.value);
              } else {
                throw new Error(error.message);
              }
            }
          });
        }
      });
  }

}
